import {
  FormDialogSubmitHandler,
  Icon,
  Password,
} from '@aibees/shared/components';
import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import { useUserChangePasswordMutationApi } from '@aibees/shared/features/user-profile/hooks/use-user-change-password-mutation-api';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack, Typography } from '@mui/material';
import { random } from 'lodash-es';
import { memo } from 'react';
import config from './config';
import { GoogleChangePassword } from './google-change-password';
import * as Styled from './styles';
import * as SX from './sx';
import type { ChangePasswordDialogProps as Props } from './types';

export const ChangePasswordDialog = memo(({ className }: Props) => {
  const notify = useNotify();

  const api = useUserChangePasswordMutationApi();

  const handleSubmit: FormDialogSubmitHandler = (data, e) => {
    const { password, currentPassword } = data;
    e.startProgress();
    api
      .mutateAsync({ password, currentPassword })
      .then(() => {
        notify.complete(
          'Password changed successfully',
          'Your new password is saved. Use it for your next login.',
          random(1, 100),
          {
            icon: notificationIconMap.Profile!,
          }
        );
        e.toggle();
      })
      .catch(e.endProgress);
  };

  const signUpWithGoogle = false; // TODO use API

  return (
    <Styled.Wrapper
      className={classnames('ChangePasswordDialog', className)}
      sx={SX.root}
    >
      <Styled.Dialog
        {...config}
        title={
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Icon name="lock-outline" color="primary.400" />
            <Typography variant="body2" fontWeight={600} color="grey.800">
              {signUpWithGoogle
                ? 'Change Password with Google Account'
                : 'Change Password'}
            </Typography>
          </Stack>
        }
        icon={signUpWithGoogle && 'alert-circle-fill'}
        actionsSx={signUpWithGoogle ? { display: 'none' } : undefined}
        uncontrolledElement={
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon name="edit-outline" />}
          >
            Edit
          </Button>
        }
        onSubmit={handleSubmit}
        submitButtonProps={{ children: 'Change' }}
        resetAfterClose
        fullWidth
        maxWidth="xs"
      >
        {signUpWithGoogle ? (
          <GoogleChangePassword />
        ) : (
          <Stack gap={1.5}>
            <Password
              name="currentPassword"
              label="Current Password"
              placeholder="Enter your current password"
              InputLabelProps={{ shrink: true }}
            />
            <Password
              name="password"
              label="New Password"
              showHint
              placeholder="Enter a new password"
              InputLabelProps={{ shrink: true }}
            />

            <Password
              name="confirmPassword"
              label="Confirm New Password"
              placeholder="Re-enter the new password"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        )}
      </Styled.Dialog>
    </Styled.Wrapper>
  );
});

ChangePasswordDialog.displayName = 'ChangePasswordDialog';
